import { Box, Chip, Tooltip } from '@mui/material'
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn'
import BuildIcon from '@mui/icons-material/Build'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { FS, V2 } from '../theme/tokens'
import { actorConfigFor, deriveActor, ACTOR_STAFF } from './actors'
import MailOutlined from '@mui/icons-material/MailOutlined'
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined'
import ChatBubbleOutlineOutlined from '@mui/icons-material/ChatBubbleOutlineOutlined'
import ForumOutlined from '@mui/icons-material/ForumOutlined'
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined'
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined'
import AssignmentReturnOutlined from '@mui/icons-material/AssignmentReturnOutlined'
import AddLinkOutlined from '@mui/icons-material/AddLinkOutlined'
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined'
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined'
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined'
import AttachFileOutlined from '@mui/icons-material/AttachFileOutlined'
import MoveToInboxOutlined from '@mui/icons-material/MoveToInboxOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'

// ── Retouren-Bahn ────────────────────────────────────────────────────────────

/**
 * Die Fallarten, die auf der Retouren-Bahn liegen.
 *
 * 🔴 SPIEGEL von `backend/rma/constants.py:1418` (`RETURN_LANE_CASE_TYPES`).
 * Aendert sich dort etwas, muss es hier mit -- die beiden sind nicht
 * mechanisch gekoppelt, und genau diese Sorte Paar ist im Repo schon mehrfach
 * auseinandergelaufen.
 *
 * ANLASS 2026-09-10: `RefundAndResolveDialog` verglich gegen `'return'`
 * ALLEIN, das Backend gegen beide Werte. Ein Widerrufsfall war damit in der
 * Oberflaeche gesperrt, obwohl die Route ihn liesse. Heute unerreichbar
 * (Bestand an prod: 0), scharf ab D13a-3.
 *
 * ⚠️ D13a-2a wird diese Konstante breit verbrauchen -- 82 harte
 * `case_type`-Vergleiche in 18 Dateien, gemessen per AST am 2026-09-10
 * (`docs/csm/25-d13a-reihenfolge-2026-09-10.md`). Sie hier anzulegen ist der
 * erste Verbraucher, nicht der einzige. Wer D13a-2a baut: hierher greifen,
 * keine zweite Fassung anlegen.
 */
export const RETURN_LANE_CASE_TYPES = ['return', 'widerruf']

// ── N39: die Fähigkeit, nicht das Etikett ────────────────────────────────────

/**
 * Ein Feld ist belegt, wenn nach dem Trimmen etwas übrig bleibt.
 *
 * 🔴 NICHT `!!wert`. An prod tragen zwei Zeilen `shopify_order_id = ''`
 * (gemessen 2026-09-22). Der Serializer reicht den leeren String durch, und
 * `!!''` ist zwar `false`, aber `' '` wäre `true` — deshalb wird getrimmt.
 */
// N41: exportiert, damit `MoneyGuardsCard` dieselbe Lesart von „belegt" benutzt
// statt eine zweite daneben zu definieren. Zwei Prädikate, die übereinstimmen
// müssen, sind der Defekt.
export const belegt = (wert) => (typeof wert === 'string' ? wert.trim() !== '' : wert != null)

/**
 * Der Fall ist in Shopify verankert: es gibt eine Bestellung oder eine Retoure,
 * gegen die eine Erstattung zu prüfen oder festzuhalten wäre.
 *
 * 🔴 SPIEGELT die Absicht von `fall_ist_erstattungsfaehig`
 * (`backend/rma/constants.py`), das Tor von `verify-refund` und
 * `mark-manually-refunded` seit N39 (2026-09-22). Dort stand bis dahin
 * `case_type not in RETURN_LANE_CASE_TYPES → 422`.
 *
 * ⚠️ Es ist eine ABSICHT, die gespiegelt wird, kein Wert. Die Zahlen kommen aus
 * demselben Prod-Lauf: die Fähigkeit öffnet 116 Fälle, die das Etikett abwies,
 * und schliesst keinen, den es zuliess (0).
 */
export const fallIstErstattungsfaehig = (c) => !!c && (
  belegt(c.shopify_order_id)
  || belegt(c.shopify_return_id)
  || RETURN_LANE_CASE_TYPES.includes(c.case_type)
)

/**
 * Über diesen Fall gibt es in Sachen Kundenerstattung ÜBERHAUPT etwas zu sagen.
 *
 * ⚠️ Bewusst ENGER als `fallIstErstattungsfaehig`, und das ist kein Versehen:
 * das Endpunkt-Tor fragt „kann man hier handeln", die Oberfläche fragt „gibt es
 * hier etwas zu zeigen". An prod trennt das 116 von 19. Eine Geldkarte auf
 * jedem Servicefall wäre neues Rauschen, keine neue Sicherheit — genau das,
 * was `MoneyGuardsCard` in ihrem eigenen Kopf ablehnt.
 *
 * Gemessen 2026-09-22: das Prädikat trifft auf den Nicht-Bahn-Fällen exakt die
 * 19 aus dem Befundzettel, alle vier lebenden darunter. Nicht getroffen:
 * 82 `service` und 16 `inquiry` ohne jede Erstattungstatsache.
 */
/**
 * Der Fall hat eine verknüpfte Shopify-Retoure.
 *
 * 🔴 EIGENES Prädikat, damit es im Repo genau EINEN Leerheitsbegriff für dieses
 * Feld gibt. Bis zum Regel-15-Tor stand im Dialog `!c.shopify_return_id` neben
 * `handwegGesperrt`, das über `belegt()` trimmt — zwei Begriffe für dieselbe
 * Frage. Bei `shopify_return_id = '  '` fallen sie auseinander, und der
 * Unerreichbarkeitsbeweis für den entfernten Sperrhinweis brach an genau dieser
 * Stelle. An prod gibt es null solcher Zeilen (gemessen 2026-09-22), der
 * Befund war also folgenlos und trotzdem einer: zwei Listen, die
 * übereinstimmen müssen, sind der Defekt.
 */
export const fallHatShopifyRetoure = (c) => !!c && belegt(c.shopify_return_id)

export const fallHatErstattungsTatsache = (c) => !!c && (
  belegt(c.shopify_return_id)
  || belegt(c.shopify_refund_id)
  || belegt(c.lsm_rt_number)
  || (belegt(c.shopify_refund_status) && c.shopify_refund_status !== 'not_required')
)

// ── Status Configuration ─────────────────────────────────────────────────────

// Humanize an unknown status key into a readable label. Keeps the UI safe when
// the DB contains a zombie status (e.g. a case rewritten to an old name by a
// stale code path, or a rename migration that only ran partially — see
// 2026-04-22 RMA-2026-0027/0034 stuck at `resolution_agreed`).
const humanizeStatus = (k) =>
  String(k || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const _STATUS_CONFIG_BASE = {
  // Service
  open:              { label: 'Open',              color: 'default' },
  // Inquiry
  draft_ready:       { label: 'Draft ready',       color: 'warning' },
  sent:              { label: 'Sent',              color: 'success' },
  manual:            { label: 'Manual',            color: 'default' },
  reopened:          { label: 'Reopened',          color: 'warning' },
  closed:            { label: 'Closed',            color: 'default' },
  with_vendor:       { label: 'With vendor',       color: 'info' },
  with_customer:     { label: 'With customer',     color: 'warning' },
  action_needed:     { label: 'Action needed',     color: 'error' },
  agreed:            { label: 'Agreed',            color: 'success' },
  awaiting_shipment: { label: 'Awaiting shipment', color: 'info' },
  approved:          { label: 'Approved',          color: 'success' },
  rejected:          { label: 'Rejected',          color: 'error' },
  resolved:          { label: 'Resolved',          color: 'success' },
  // Return workflow
  draft:             { label: 'Draft',             color: 'default' },
  return_requested:  { label: 'Return requested',  color: 'warning' },
  return_created:    { label: 'Return created',    color: 'default' },
  awaiting_return:   { label: 'Awaiting return',   color: 'info' },
  in_transit:        { label: 'In transit',        color: 'info' },
  received:          { label: 'Received',          color: 'success' },
  booked:            { label: 'Booked',            color: 'success' },
  // Terminal
  cancelled:         { label: 'Cancelled',         color: 'default' },
  // ── Privacy / GDPR request queue (2026-07-24) ───────────────────────────
  requested:         { label: 'Requested',         color: 'warning' },
  executed:          { label: 'Executed',          color: 'success' },
  // ── Withdrawal § 356a BGB (staff-approval cancel flow, 2026-06-10) ──────
  // Cases with case_type='return' + shopify_return_reason='withdrawal_eu_356a'.
  // Created at widerruf routing time (backend/widerruf/cancel_approval.py
  // ::create_widerruf_rma_case). The 3 statuses below mirror plan §3 routing
  // matrix outcomes.
  // manual_review = warning per pog-ux-review: "auto-pipeline failed, staff
  // must decide" maps to action-awaited bucket (like with_customer / reopened).
  pending_widerruf_approval: { label: 'Withdrawal: approval pending', color: 'warning' },
  widerruf_manual_review:    { label: 'Withdrawal: manual review',    color: 'warning' },
  widerruf_inconsistent:     { label: 'Withdrawal: inconsistent',     color: 'error'   },
}

// Proxy wraps STATUS_CONFIG so any unknown key falls back to a humanized
// label + neutral colour instead of leaking the raw key into the UI.
// Callers use `STATUS_CONFIG[status]` exactly as before — no caller change.
export const STATUS_CONFIG = new Proxy(_STATUS_CONFIG_BASE, {
  get(target, prop) {
    if (typeof prop !== 'string') return Reflect.get(target, prop)
    if (prop in target) return target[prop]
    return { label: humanizeStatus(prop), color: 'default' }
  },
  has(target, prop) {
    return typeof prop === 'string' ? true : Reflect.has(target, prop)
  },
})

// Same fallback for direct label lookups.
const _STATUS_LABELS_BASE = Object.fromEntries(
  Object.entries(_STATUS_CONFIG_BASE).map(([k, v]) => [k, v.label])
)
export const STATUS_LABELS = new Proxy(_STATUS_LABELS_BASE, {
  get(target, prop) {
    if (typeof prop !== 'string') return Reflect.get(target, prop)
    return prop in target ? target[prop] : humanizeStatus(prop)
  },
})

export const OPEN_STATUSES = [
  'open', 'draft', 'return_requested', 'return_created',
  'with_vendor', 'with_customer', 'action_needed', 'approved',
  'agreed', 'awaiting_shipment',
  'awaiting_return', 'in_transit', 'received', 'booked',
  // Widerruf staff-approval cancel flow — all 3 are open work
  'pending_widerruf_approval', 'widerruf_manual_review', 'widerruf_inconsistent',
]

export const SERVICE_STATUSES = [
  'open', 'with_vendor', 'with_customer', 'action_needed',
  'agreed', 'awaiting_shipment', 'approved', 'rejected', 'resolved', 'cancelled',
]
export const RETURN_STATUSES = [
  'draft', 'return_requested', 'return_created', 'awaiting_return', 'in_transit',
  'received', 'booked', 'approved', 'rejected', 'resolved', 'cancelled',
  // Widerruf: cases live on case_type='return' per plan L9 discriminator
  // shopify_return_reason='withdrawal_eu_356a'.
  'pending_widerruf_approval', 'widerruf_manual_review', 'widerruf_inconsistent',
]
export const TERMINAL_STATUSES = ['resolved', 'rejected', 'cancelled']

// ── Age alarm threshold ───────────────────────────────────────────────────────
//
// The Age column of the case list is three-tiered (concepts/cases.md §2.5):
// 0–7 quiet, 8–29 normal, ≥ 30 in `error.main` with a `d` suffix. The threshold
// is a named constant and not a literal in the cell, so the number is arguable
// in ONE place — the same reason the status vocabulary lives in this file.
//
// 🔴 It is deliberately NOT `is_overdue`. `is_overdue` is an SLA predicate
// (backend/rma_sla.py) that answers "is this case in breach" with a boolean;
// the column has to answer "how bad is it". A 1-day breach and a 49-day breach
// rendered as the identical `error.main` at the identical weight — measured in
// the audit (concepts/cases.md §0.1). Breach is stated by the `Due` chip next
// to the status; elapsed time is stated by this column.
export const AGE_ALARM_DAYS = 30

export const INQUIRY_STATUSES = [
  'open', 'draft_ready', 'manual', 'sent', 'reopened', 'closed',
]

// ── Inquiry Types ─────────────────────────────────────────────────────────────
export const INQUIRY_TYPE_LABELS = {
  product_advisory: 'Product Advisory',
  delivery_status:  'Delivery Status',
  return_policy:    'Return Policy',
  invoice:          'Invoice',
  general:          'General',
  gdpr_deletion_request: 'Deletion request (GDPR)',
}

// ── Issue Types ──────────────────────────────────────────────────────────────

export const ISSUE_TYPE_LABELS = {
  return:               'Return',
  defective_doa:        'Dead on Arrival',
  defective_after_use:  'Defective After Use',
  wrong_item:           'Wrong Item',
  missing_part:         'Missing Part',
  cosmetic_damage:      'Cosmetic Damage',
  packaging_damage:     'Packaging Damage',
  other:                'Other',
  // Sentinel for inquiry-type cases (no RMA defect) — written by the manual
  // wizard path and the inbox auto-create. rma_cases.issue_type is NOT NULL,
  // so inquiries never carry a null issue_type.
  inquiry:              'Inquiry',
  // Created by backend/widerruf/cancel_approval.py::create_widerruf_rma_case
  // when a § 356a BGB widerruf lands in the unfulfilled-cancel path. Combined
  // with shopify_return_reason='withdrawal_eu_356a' this is the canonical
  // discriminator for widerruf-spawned RMA cases.
  widerruf:             'Withdrawal § 356a BGB',
}

// ── N2a (2026-09-07): der Spiegel von backend/rma/constants.py::ISSUE_TYPES ──
// Eine JS-Datei kann kein Python importieren, also bleibt ISSUE_TYPE_LABELS
// oben die Quelle DIESER Seite — gebunden an die andere durch
// `scripts/check_issue_type_wertemenge.py`, in BEIDE Richtungen und
// einschliesslich der Reihenfolge (sie treibt zwei Auswahllisten).
// 🔴 Der WORTLAUT weicht bei fuenf Werten vom Backend ab („Dead on Arrival"
// gegen „Dead on Arrival (DOA)"). Der Waechter bindet die Schluessel, nicht
// den Wortlaut; die Angleichung ist N2b, weil sie eine CSV-Spalte und eine
// Lieferanten-Mailvorlage veraendert.

// Warum der Anlageassistent `widerruf` nicht anbietet: ein Widerruf entsteht
// in `backend/widerruf/cancel_approval.py`, nicht von Hand. Das war richtig
// und stand nirgends im Code (A4 der Inventur, 🟡 „vermutlich Absicht").
export const ISSUE_TYPES_NICHT_MANUELL_ANLEGBAR = new Set(['widerruf'])

// Etikett und Gruppe, die der ANLAGEASSISTENT beitraegt — die Wertemenge tut
// er nicht mehr selbst (Sammlung Nr. 10 der Inventur). Wer oben einen Wert
// ergaenzt und ihn hier vergisst, faellt beim Waechter auf.
// 🔴 Beobachtbar sind nur die sieben `Service Case`-Eintraege:
// `RMANewCaseWizardContent.jsx` ist der einzige Verbraucher und filtert auf
// genau diese Gruppe. `return` kommt aus der Kachel, `inquiry` aus der
// Anfragen-Bahn — die zwei Eintraege werden nie gerendert. Sie bleiben
// wertgleich stehen; ihr Abbau ist ein eigener Befund, kein Nebeneffekt.
export const WIZARD_ISSUE_TYPE_ANNOTATION = {
  return:               { label: 'Return',                     group: 'Return' },
  defective_doa:        { label: 'Dead on Arrival (DOA)',      group: 'Service Case' },
  defective_after_use:  { label: 'Defective After Use',        group: 'Service Case' },
  wrong_item:           { label: 'Wrong Item Sent',            group: 'Service Case' },
  missing_part:         { label: 'Missing Part / Accessory',   group: 'Service Case' },
  cosmetic_damage:      { label: 'Cosmetic Damage',            group: 'Service Case' },
  packaging_damage:     { label: 'Packaging Damage Only',      group: 'Service Case' },
  other:                { label: 'Other',                      group: 'Service Case' },
  inquiry:              { label: 'Question / Inquiry',         group: 'Inquiry' },
}

// ── N2b (2026-09-07): EINE Darstellungsregel fuer `issue_type` ───────────────
//
// Die Inventur (`docs/csm/12-issue-type-inventur-2026-09-05.md`, Achse 1) hat
// VIER Darstellungsregeln fuer dasselbe Feld gezaehlt. Gegen den
// zusammengefuehrten Baum nachgezaehlt waren es **sieben**:
//
//   R1  `ISSUE_TYPE_LABELS[v] || v || '—'`      Fallkopf, Fall-Infofeld, Shopify-Modal
//   R2  `ISSUE_TYPE_LABELS[k] || k`             Berichte (2x) — und `macros.js`, siehe unten
//   R3  `ISSUE_TYPE_LABELS[t] || t || 'Other'`  Zusatzthemen-Chip (+ eine Variante ohne Fallback)
//   R4  `v.replace(/_/g, ' ')`, OHNE Karte      Posteingang UND Assistenten-Panel
//   R5  roh, ohne alles                          Zeitstrahl (2x)
//   R6  Dreiweg-Ternaer auf die Fallart          Anlageassistent (7 Vorkommen)
//   R7  `intent.replace(/_/g, ' ')` im Tooltip   ConfidenceBadge, gefuettert aus den Zusatzthemen
//
// Ab hier ist es EINE: `issueTypeAnzeige` fuer den Text, `fallartAusIssueType`
// + `WIZARD_FALLART_CHIP` fuer die Fallart.
//
// 🔴 **Warum ein unbekannter Wert ROH bleibt und NICHT humanisiert wird.**
// Naheliegend waere das Muster von `humanizeStatus` oben. Gemessen an prod
// (2026-09-07, lesend ueber `backend/tools/prod_lesen.py`) haelt es hier nicht:
//
//   * unbekannte Werte, die es wirklich gibt: `defective` (1 Fall),
//     `defect` (1 Posteingangszeile). Zusammen 2 von 1011 gemessenen Zeilen.
//   * unbekannte Werte MIT Unterstrich — der einzige Fall, in dem
//     `_`->Leerzeichen ueberhaupt etwas taete —, ueber Faelle, Posteingang und
//     Ereignisse zusammen: **NULL**.
//     Positivkontrolle derselben Abfrageform ueber BEKANNTE Werte: 5 Werte,
//     70 Zeilen. Die Abfrage misst also, was sie messen soll.
//
// Der `_`->Leerzeichen-Zweig hat damit nie einen unbekannten Wert getroffen; er
// hat ausschliesslich BEKANNTE Werte kosmetisch aufbereitet, und fuer die
// liefert die Etikettenkarte den besseren Text. Ihn fallen zu lassen verliert
// nichts — und es erhaelt das, was der Posteingang richtig hatte: er zeigt
// einen LLM-Wert, der noch kein Fall ist. Ein Wert, den die Karte nicht kennt,
// bleibt dort als Fremdkoerper erkennbar (`defect`, nicht `Defect`) statt wie
// ein gepflegtes Etikett auszusehen.
//
// 🔴 **Was diese Regel NICHT anfasst: den WORTLAUT.** `ISSUE_TYPE_LABELS`
// fuettert ueber `rma/components/ComposeEditor.jsx` ->
// `common/SmartCompose/ComposeEditor.jsx` -> `common/SmartCompose/macros.js`
// die Variable `issue_type` des Slash-Menues, und die landet im **Mailkoerper
// an Kunde oder Lieferant**. `DEFAULT_FEATURES.macros` ist `true` und kein
// Aufrufer schaltet es ab (gegrept: 0 Treffer auf `features=`). Eine
// Wortlaut-Angleichung ist damit eine Aenderung an Kundentext (Regel 5f) und
// gehoert in einen eigenen Zug mit eigener Abwaegung — NICHT hierher.
// Der N2a-Nachtrag nennt diese Haelfte „die billigste des Pakets, null
// Backend-Verbraucher"; das stimmt fuer das BACKEND und uebersieht diesen Pfad.
//
// `macros.js` bleibt deshalb bewusst bei seinem eigenen
// `issueTypeLabels[v] || v` und benutzt `issueTypeAnzeige` NICHT — bewacht als
// eigene Regel `macros_roh` in `scripts/check_issue_type_wertemenge.py`.

/**
 * Die eine Anzeigeregel fuer `issue_type`.
 *
 * @param {string|null|undefined} wert  roher `issue_type` (DB-Wert oder LLM-Wert)
 * @param {{fallback?: *}} [opt]  was ein LEERER Wert rendert. `'—'` fuer eine
 *   Tabellenzelle, `null` fuer eine Zeile, die bei fehlendem Wert ganz
 *   verschwindet, der Schluessel selbst fuer eine Diagrammlegende.
 * @returns {*} das kanonische Etikett, sonst der ROHE Wert, sonst `fallback`.
 */
export const issueTypeAnzeige = (wert, { fallback = '—' } = {}) => {
  if (wert == null || wert === '') return fallback
  return ISSUE_TYPE_LABELS[wert] || wert
}

/**
 * Kennt die Wertemenge diesen `issue_type`? (D12, 2026-09-09)
 *
 * 🔴 **Das ist KEINE Anzeigeregel** — genau deshalb steht sie hier als eigene
 * Funktion und nicht als `ISSUE_TYPE_LABELS[x]` an der Aufrufstelle. Der
 * Waechter `scripts/check_issue_type_wertemenge.py` (Regel `anzeige_adoption`)
 * hat den direkten Klammerzugriff in `RMAInbox.jsx` prompt als achte
 * Darstellungsregel gemeldet, und er hatte recht: ein Klammerzugriff sieht wie
 * eine Etikettensuche aus, auch wenn er als Zugehoerigkeitstest gemeint ist.
 *
 * Der Spiegel im Backend ist `backend/rma/constants.py::issue_type_oder_sentinel`.
 * Er tut mehr (er liefert den Sentinel und den verworfenen Rohwert); hier wird
 * nur die Frage gebraucht.
 *
 * 🔴 **Sie fragt die ENGE Menge, nicht die weite** (Regel-16-Gate, 09.09.).
 * `widerruf` steht in `ISSUE_TYPE_LABELS`, aber der Anlageassistent bietet ihn
 * bewusst NICHT an (`ISSUE_TYPES_NICHT_MANUELL_ANLEGBAR` — ein Widerruf entsteht
 * in `backend/widerruf/cancel_approval.py`, nicht von Hand). Ohne das dritte
 * Konjunkt liefe genau das Fehlerbild wieder, gegen das D12 angetreten ist, nur
 * auf einem anderen Wert: Vorbelegung greift -> das Auswahlfeld enthaelt den
 * Wert nicht -> es sieht LEER aus, waehrend `issueType` wahr ist ->
 * `disabled={!issueType}` ist falsch -> „Next" aktiv -> ein von Hand angelegter
 * Widerrufsfall, der die Widerrufsbahn umgeht.
 *
 * @param {string|null|undefined} wert  roher `issue_type`
 * @returns {boolean} true, wenn der Wert deklariert UND von Hand waehlbar ist
 */
export const istBekannterIssueType = (wert) =>
  typeof wert === 'string' &&
  Object.prototype.hasOwnProperty.call(ISSUE_TYPE_LABELS, wert) &&
  !ISSUE_TYPES_NICHT_MANUELL_ANLEGBAR.has(wert)

/**
 * Die eine Fallart-Ableitung des Anlageassistenten.
 *
 * Sie war siebenmal als Ternaer ausgeschrieben (R6): dreimal als
 * „Service" (`issueType && issueType !== 'return' && issueType !== 'inquiry'`),
 * zweimal als „Return" und zweimal im Chip.
 *
 * ⚠️ **Der LEERE Wert.** `''` heisst im Assistenten „noch nichts gewaehlt" und
 * NICHT „Service". `fallartAusIssueType('')` gibt trotzdem `'service'` zurueck —
 * die Funktion beantwortet die Frage „welche Fallart traegt dieser Wert", nicht
 * „hat der Benutzer schon gewaehlt". Die Kachelausdruecke behalten deshalb
 * ihren eigenen Leer-Test (`issueType && …`) als eigenes Konjunkt. Der Chip
 * braucht ihn nicht: `''` ergibt dort `Service`/`info` — genau der heutige
 * Else-Zweig. Bewacht von `anzeige_adoption`.
 *
 * @param {string|null|undefined} wert
 * @returns {'return'|'inquiry'|'service'}
 */
export const fallartAusIssueType = (wert) =>
  wert === 'return' ? 'return' : wert === 'inquiry' ? 'inquiry' : 'service'

/** Text und Farbe des Fallart-Chips im Anlageassistenten (wertgleich zu R6). */
export const WIZARD_FALLART_CHIP = {
  return:  { label: 'Return',  color: 'warning'   },
  inquiry: { label: 'Inquiry', color: 'secondary' },
  service: { label: 'Service', color: 'info'      },
}

// G8 der Inventur: dieselbe Landkarte lag im Backend (7 Eintraege) und in der
// Vorschau (5). Beide fielen auf 'OTHER' zurueck, waren also wertgleich —
// getrennt gepflegt waren sie trotzdem. Sie steht jetzt einmal je Sprache und
// der Waechter haelt die zwei Fassungen PAARWEISE aneinander, nicht nur ueber
// die Schluessel. Gegenstueck: `backend/rma/constants.py::
// SHOPIFY_RETURN_REASON_BY_ISSUE_TYPE`.
// 🟢 Seit 2026-09-25 (N7) teilen sie auch das Tor davor: `shopifyReasonFor`
// bekommt `kundeZahlt` (`!== false`), das Backend liest `kunde_traegt_rueckporto`.
// Auf `null` sagen beide „der Kunde zahlt" (Owner B2). Bis dahin las es roh.
export const SHOPIFY_REASON_BY_ISSUE = {
  defective_doa: 'DEFECTIVE',
  defective_after_use: 'DEFECTIVE',
  cosmetic_damage: 'DEFECTIVE',
  packaging_damage: 'DEFECTIVE',
  wrong_item: 'WRONG_ITEM',
  missing_part: 'OTHER',
  other: 'OTHER',
}
export const SHOPIFY_REASON_DEFAULT = 'OTHER'

// ── RMA resolution axes (migration 118 / 2026-05-15) ────────────────────────
// Two orthogonal axes:
//   customer_resolution = WHAT the customer ends up holding (content)
//   service_outcome     = WHO/WHERE physical movement happens (logistics)
// Mirror of backend/rma/constants.py. Read paths normalize legacy → canonical.
// Write paths emit canonical only. Legacy values supported one release.

// ── Customer Resolution (WHAT — content axis) ───────────────────────────────

// 🔴 D8 - Ausgangsvokabular. Owner-Entscheid E23 vom 2026-09-22.
//
// SPIEGEL von `backend/rma/constants.py::CUSTOMER_RESOLUTION_VALUES`. Seit D8
// haelt `scripts/check_ausgangsvokabular_beide_seiten.py` die beiden Fassungen
// in BEIDEN Richtungen aneinander: ein Wert hier ohne Gegenstueck dort ist
// genauso ein Befund wie umgekehrt. Vorher fror jede Seite nur ihre EIGENE
// Liste ein, und niemand mass die Uebereinstimmung.
//
// Warum 'Retoure' hier KEIN Wert ist, obwohl der Owner sie zuerst nennt: die
// Retoure ist eine BEWEGUNG und steht auf der Logistikachse
// (`SERVICE_OUTCOME_OPTIONS`). `customer_resolution` sagt, WAS der Kunde am
// Ende haelt. Ein Bewegungswort auf der Inhaltsachse braechte genau die
// Verwechslung von Etikett und Sache zurueck, die die Bahn N39 entfernt hat.
// Die ganze Begruendung steht an der Wertemenge im Backend.
//
// Die Beschriftungen bleiben englisch, weil die Liste hier englisch ist.
export const CUSTOMER_RESOLUTION_OPTIONS = [
  { value: 'full_replacement', label: 'Full replacement' },
  { value: 'spare_parts',      label: 'Spare parts' },
  { value: 'refund',           label: 'Full refund' },
  { value: 'store_credit',     label: 'Store credit / voucher' },
  { value: 'pending',          label: 'Not yet decided' },
  // ── ab hier D8 (E23, 2026-09-22) ──
  { value: 'partial_refund',   label: 'Partial refund' },
  { value: 'no_action_needed', label: 'No action needed' },
  { value: 'other',            label: 'Other' },
]

// ── Service Outcome (WHO/WHERE — logistics axis) ────────────────────────────
// Promoted from local definition in TransitionSheet.jsx so all consumers
// (TransitionSheet, RMADetail, ResolutionPanel) share one list.
// 🔴 `CaseStatusBar` stand hier bis 2026-09-08 als vierter Verbraucher; die
// Komponente ist mit Block 2 Stufe 3a gelöscht.

export const SERVICE_OUTCOME_OPTIONS = [
  { value: 'ship_from_our_stock',       label: 'Ship from our stock' },
  { value: 'vendor_direct_to_customer', label: 'Vendor → customer (direct)' },
  { value: 'vendor_to_us_to_customer',  label: 'Vendor → us → customer' },
  { value: 'no_shipping_needed',        label: 'No shipping needed' },
  { value: 'other',                     label: 'Other' },
]

export const LEGACY_SERVICE_OUTCOME_MAP = {
  replacement_via_us:     'ship_from_our_stock',
  replacement_direct:     'vendor_direct_to_customer',
  replacement_via_vendor: 'vendor_to_us_to_customer',
  refund:                 'no_shipping_needed',
  spare_parts:            'ship_from_our_stock',
  credit_deduction:       'no_shipping_needed',
}

export const LEGACY_CUSTOMER_RESOLUTION_MAP = {
  replacement: 'full_replacement',
}

const CUSTOMER_RESOLUTION_VALUES = new Set(CUSTOMER_RESOLUTION_OPTIONS.map(o => o.value))
const SERVICE_OUTCOME_VALUES = new Set(SERVICE_OUTCOME_OPTIONS.map(o => o.value))

export const NO_RETURN_RESOLUTIONS = new Set([
  'full_replacement', 'store_credit', 'spare_parts',
])
// 🔴 D8-Torrunde: ALLE DREI neuen Werte stehen hier ABSICHTLICH NICHT, und der
// dritte ist erst nachtraeglich herausgenommen worden. Die Begruendung haengt
// nicht an den Werten, sondern an den VERBRAUCHERN dieser Menge - im Backend
// bewegen sie Geld und Shopify-Zustand (§356a-Netz, Stornierung der
// Shopify-Retoure, Erstattungstor). Vollstaendig an der Wertemenge in
// `backend/rma/constants.py::NO_RETURN_RESOLUTIONS`.
//
// `no_action_needed` - 'es passiert nichts, also kommt nichts zurueck' stimmt
//   fuer den Warenfluss und war die Aufnahmebegruendung. Die Verbraucher wurden
//   dabei nicht gemessen. Herausnehmen ist die umkehrbare Richtung; eine
//   stornierte Shopify-Retoure ist terminal.
// `partial_refund` - eine Teilerstattung kann sehr wohl auf eine Retoure
//   folgen. Hier eingetragen wuerde sie die Shopify-Retoure absagen.
// `other` - unbekannt darf nicht als 'keine Retoure' gelten; das ist die
//   unsichere Richtung. Abwesenheit heisst hier nur 'wir wissen es nicht'.

export const SERVICE_OUTCOMES_WE_SHIP = new Set([
  'ship_from_our_stock', 'vendor_to_us_to_customer',
])

export function normalizeServiceOutcome(v) {
  if (v == null) return null
  if (SERVICE_OUTCOME_VALUES.has(v)) return v
  return LEGACY_SERVICE_OUTCOME_MAP[v] || v
}

export function normalizeCustomerResolution(v) {
  if (v == null) return null
  if (CUSTOMER_RESOLUTION_VALUES.has(v)) return v
  return LEGACY_CUSTOMER_RESOLUTION_MAP[v] || v
}

export function isNoReturnResolution(v) {
  const canonical = normalizeCustomerResolution(v)
  return canonical != null && NO_RETURN_RESOLUTIONS.has(canonical)
}

export function isWeShipOutcome(v) {
  const canonical = normalizeServiceOutcome(v)
  return canonical != null && SERVICE_OUTCOMES_WE_SHIP.has(canonical)
}

// TransitionSheet interlock: default + allowed-set per customer_resolution.
export const DEFAULT_OUTCOME_FOR = {
  full_replacement: 'ship_from_our_stock',
  spare_parts:      'ship_from_our_stock',
  refund:           'no_shipping_needed',
  store_credit:     'no_shipping_needed',
  // D8: eine Teilerstattung bewegt nichts - wie `refund`.
  partial_refund:   'no_shipping_needed',
  // D8: nichts passiert, also bewegt sich auch nichts.
  no_action_needed: 'no_shipping_needed',
  // pending → no default (staff still investigating)
  // other   → ebenfalls KEIN Vorgabewert (D8): der Auffangwert sagt nichts
  //           ueber die Logistik, und eine Vorgabe waere hier eine Behauptung.
}

export const ALLOWED_OUTCOMES_FOR = {
  full_replacement: new Set(['ship_from_our_stock', 'vendor_direct_to_customer', 'vendor_to_us_to_customer', 'other']),
  spare_parts:      new Set(['ship_from_our_stock', 'vendor_direct_to_customer', 'vendor_to_us_to_customer', 'other']),
  refund:           new Set(['no_shipping_needed']),
  store_credit:     new Set(['no_shipping_needed']),
  // D8: eine Teilerstattung faellt mit `refund` zusammen - Geld zurueck,
  // nichts bewegt sich.
  partial_refund:   new Set(['no_shipping_needed']),
  // D8: nichts passiert. Jeder andere Ausgang waere ein Widerspruch zum Wort.
  no_action_needed: new Set(['no_shipping_needed']),
  // pending: empty set — staff still investigating, no logistics decision yet.
  // TransitionSheet additionally disables the Service Outcome select on pending
  // (UX defense in depth — `disabled` + no allowed options + no default).
  pending:          new Set(),
  // 🔴 D8: `other` ist der AUFFANGWERT und laesst deshalb ALLE fuenf
  // Logistikwege zu. Er wird aus `SERVICE_OUTCOME_OPTIONS` abgeleitet und
  // nicht abgeschrieben: eine zweite Liste, die mit der ersten uebereinstimmen
  // muss, ist im Haus schon mehrfach auseinandergelaufen.
  other:            new Set(SERVICE_OUTCOME_OPTIONS.map(o => o.value)),
}

// 🔴 D8: welche Ausgaenge GAR KEINEN Logistikweg zulassen - ausdruecklich
// benannt statt aus der leeren Menge erschlossen.
//
// Ohne diese Erklaerung waere eine versehentlich geleerte Zeile in
// `ALLOWED_OUTCOMES_FOR` von einer gewollt leeren nicht zu unterscheiden, und
// der Waechter `scripts/check_ausgangsvokabular_beide_seiten.py` koennte die
// Vollstaendigkeit des Verriegelungstisches nicht pruefen. Wer einen Ausgang
// ohne Logistikweg baut, traegt ihn hier ein - das ist eine Entscheidung und
// soll eine bleiben. Hausmuster: `VENDOR_INTENT_UNENTSCHIEDEN` im Backend.
export const INTERLOCK_OHNE_AUSGANG = new Set(['pending'])

// 🔴 D8-Torrunde (B5): ein Wert, den der Verriegelungstisch NICHT kennt, bekam
// bisher ALLE fuenf Logistikwege.
//
// `ALLOWED_OUTCOMES_FOR[wert]` ist fuer einen unbekannten Wert `undefined`, und
// die Oberflaeche las das als 'keine Einschraenkung'. Fehlender Eintrag hiess
// damit maximale Freiheit - die unsichere Richtung. Prod fuehrt drei Faelle mit
// dem Lagerwert `relist` auf `customer_resolution`; sie bekamen jeden
// Logistikweg offen, samt Widerspruechen wie 'Ersatz ohne Versand'.
//
// Ab jetzt: unbekannt bekommt dieselbe Behandlung wie `pending` - KEINEN Weg.
// Das ist die umkehrbare Richtung: wer einen Ausgang wirklich braucht, traegt
// ihn in den Tisch ein, und der Waechter
// `scripts/check_ausgangsvokabular_beide_seiten.py` haelt ihn dann gegen das
// Vokabular.
//
// ⚠️ NICHT gewaehlt (leer/null) bleibt ausdruecklich wie bisher unbeschraenkt.
// 'noch nichts gewaehlt' ist etwas anderes als 'etwas Unbekanntes gewaehlt',
// und die bestehenden Zuege setzen die Logistik haeufig vor der Inhaltsachse.

/** True, wenn ein GESETZTER customer_resolution im Verriegelungstisch fehlt. */
function istUnbekannterAusgang(customerResolution) {
  if (customerResolution == null || customerResolution === '') return false
  const kanonisch = normalizeCustomerResolution(customerResolution)
  return !Object.prototype.hasOwnProperty.call(ALLOWED_OUTCOMES_FOR, kanonisch)
}

/**
 * Die erlaubten Logistikwege fuer einen customer_resolution.
 *
 * - nichts gewaehlt  -> `undefined` (keine Einschraenkung, wie bisher)
 * - bekannter Wert   -> sein Eintrag aus `ALLOWED_OUTCOMES_FOR`
 * - unbekannter Wert -> die LEERE Menge, also dieselbe wie `pending`
 */
export function allowedOutcomesFor(customerResolution) {
  if (customerResolution == null || customerResolution === '') return undefined
  const kanonisch = normalizeCustomerResolution(customerResolution)
  const eintrag = ALLOWED_OUTCOMES_FOR[kanonisch]
  if (eintrag) return eintrag
  return ALLOWED_OUTCOMES_FOR.pending
}

/**
 * True, wenn die Logistikachse ganz gesperrt gehoert: `pending` (Personal
 * ermittelt noch) und jeder unbekannte Wert (wir wissen nicht, was er bedeutet).
 */
export function isInterlockGesperrt(customerResolution) {
  const kanonisch = normalizeCustomerResolution(customerResolution)
  if (kanonisch != null && INTERLOCK_OHNE_AUSGANG.has(kanonisch)) return true
  return istUnbekannterAusgang(customerResolution)
}

export const RESOLUTION_TYPE_OPTIONS = [
  { value: 'credit_note',       label: 'Credit Note' },
  { value: 'replacement',       label: 'Replacement Unit' },
  { value: 'next_order_credit', label: 'Credit Against Next Order' },
  { value: 'rejected',          label: 'Rejected (No Compensation)' },
]

// ── Stock Disposition (migration 081) ───────────────────────────────────────
// Orthogonal to customer_resolution. Describes what happens to the physical
// returned item, NOT what the customer gets.
export const STOCK_DISPOSITION_LABELS = {
  relist:    'Relist (A-grade)',
  refurbish: 'Refurbish',
  scrap:     'Scrap',
}

export const STOCK_DISPOSITION_COLORS = {
  relist:    { bg: V2.okSoft, fg: V2.okText },  // green
  refurbish: { bg: V2.warnSoft, fg: V2.warnText },  // amber
  scrap:     { bg: V2.errSoft, fg: V2.errText },  // red
}

export const STOCK_DISPOSITION_OPTIONS = [
  { value: 'relist',    label: 'Relist (A-grade, pristine)' },
  { value: 'refurbish', label: 'Refurbish' },
  { value: 'scrap',     label: 'Scrap' },
]

// ── Shared Components ────────────────────────────────────────────────────────

export function StatusChip({ status, size = 'small' }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'default' }
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size={size}
      sx={{ fontSize: FS.xs, height: size === 'small' ? 22 : undefined }}
    />
  )
}

/**
 * Die Anzeigebezeichnung der Fallart - EINE Quelle fuer Badge, Symbol und die
 * neue Fallansicht (Owner-Entscheid 34: *„Rohe Werte im Text korrigieren"*).
 *
 * Bis 2026-10-11 stand dieselbe Dreier-Regel zweimal hier (in `CaseTypeBadge`
 * und in `CaseTypeIcon`); die neue Fallansicht brauchte sie ein drittes Mal.
 * Ein drittes Literal waere die Sorte Kopie, die beim ersten Umbau
 * auseinanderlaeuft - also steht sie einmal und wird gelesen.
 */
export function caseTypeLabel(caseType) {
  if (caseType === 'return') return 'Return'
  if (caseType === 'inquiry') return 'Inquiry'
  return 'Service'
}

export function CaseTypeBadge({ caseType, size = 'small' }) {
  const isReturn = caseType === 'return'
  const isInquiry = caseType === 'inquiry'
  const label = caseTypeLabel(caseType)
  const color = isReturn ? 'warning.main' : isInquiry ? 'secondary.main' : 'info.main'
  return (
    <Chip
      label={label}
      size={size}
      variant="outlined"
      sx={{
        fontSize: FS.xs,
        height: 20,
        color: color,
        borderColor: color,
      }}
    />
  )
}

// Compact icon-only variant — use inside dense cells (e.g. the case-name
// column of the overview list) where the explicit chip is too heavy. The
// tooltip surfaces the full label on hover. Returns / Service / Inquiry
// keep the same color tokens as the badge for consistency.
export function CaseTypeIcon({ caseType, fontSize = 14 }) {
  const isReturn = caseType === 'return'
  const isInquiry = caseType === 'inquiry'
  const label = caseTypeLabel(caseType)
  const color = isReturn ? 'warning.main' : isInquiry ? 'secondary.main' : 'info.main'
  const Icon = isReturn ? AssignmentReturnIcon : isInquiry ? HelpOutlineIcon : BuildIcon
  return (
    <Tooltip title={label} arrow placement="top">
      <Icon sx={{ fontSize, color, flexShrink: 0 }} aria-label={label} />
    </Tooltip>
  )
}

// ── Actor badge ─────────────────────────────────────────────────────────────
// Loud, single-glance signal of whose turn it is. Two variants:
//   - "chip" (default): compact, table/row-friendly.
//   - "strip": hero-bar pill with pulsing dot when staff must act.
// Shared primitive — used on the case hero, overview table, inbox row, and
// the future "Your open tasks" view. Derivation lives in rma/actors.js.

export function ActorBadge({ caseData, size = 'small', variant = 'chip' }) {
  const cfg = actorConfigFor(caseData)
  const actor = deriveActor(caseData)
  const isStaff = actor === ACTOR_STAFF
  if (variant === 'strip') {
    return (
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75,
        px: 1.25, py: 0.5, borderRadius: 99,
        bgcolor: cfg.bg, color: cfg.accent,
        fontSize: FS.xs, fontWeight: 700, letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}>
        <Box sx={{
          width: 6, height: 6, borderRadius: '50%',
          bgcolor: cfg.accent,
          animation: isStaff ? 'actor-pulse 2s ease-in-out infinite' : 'none',
          '@keyframes actor-pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.4 },
          },
        }} />
        <span>{cfg.label}</span>
      </Box>
    )
  }
  return (
    <Chip
      label={cfg.shortLabel}
      size={size}
      color={cfg.chipColor}
      variant={isStaff ? 'filled' : 'outlined'}
      sx={{
        fontSize: FS.xs,
        height: 20,
        fontWeight: isStaff ? 700 : 500,
      }}
    />
  )
}

// ── Communication Type Labels ────────────────────────────────────────────────

export const COMM_LABELS = {
  email_sent:               'Email sent',
  email_template_copied:    'Email copied',
  vendor_response_logged:   'Vendor reply',
  customer_response_logged: 'Customer reply',
  resolution_email_sent:    'Resolution email sent',
  lsm_return_update:        'LSM return update',
  shopify_return_created:   'Shopify return created',
  shopify_return_closed:    'Shopify return closed',
  shopify_return_deferred:  'Shopify return open (refund pending)',
  shopify_return_linked:    'Shopify return linked',
  status_change:            'Status change',
  note:                     'Note',
  file_added:               'File added',
  whatsapp_outbound_manual: 'WhatsApp sent (manual)',
  kanal_outbound_manual:    'Message sent via channel (manual)',
  internal_to_vendor:       'Message to vendor',
  internal_to_customer:     'Message to customer',
  internal_outbound:        'Outbound message',
}

// ── Communication Icon Config ───────────────────────────────────────────────

export const COMM_ICON_MAP = {
  email_sent:               MailOutlined,
  email_template_copied:    ContentCopyOutlined,
  vendor_response_logged:   ChatBubbleOutlineOutlined,
  customer_response_logged: ForumOutlined,
  resolution_email_sent:    MarkEmailReadOutlined,
  lsm_return_update:        LocalShippingOutlined,
  shopify_return_created:   AssignmentReturnOutlined,
  shopify_return_closed:    CheckCircleOutlineOutlined,
  shopify_return_deferred:  AssignmentReturnOutlined,
  // AddLink, not AssignmentReturn: "linked" IS the semantic, and reusing
  // shopify_return_created's glyph + blue would make the two rows visually
  // identical in a stream where both can appear. LinkOff is already the repo's
  // unlink glyph, so this keeps the link/unlink pair coherent.
  shopify_return_linked:    AddLinkOutlined,
  status_change:            SwapHorizOutlined,
  note:                     StickyNote2Outlined,
  file_added:               AttachFileOutlined,
  whatsapp_outbound_manual: WhatsAppIcon,
  kanal_outbound_manual:    ForumOutlined,
  _inbound:                 MoveToInboxOutlined,
  internal_to_vendor:       MailOutlined,
  internal_to_customer:     MailOutlined,
  internal_outbound:        MailOutlined,
}

export const COMM_ICON_COLORS = {
  email_sent:               { border: V2.infoText, bg: V2.infoSoft },
  email_template_copied:    { border: V2.infoText, bg: V2.infoSoft },
  vendor_response_logged:   { border: V2.okText, bg: V2.okSoft },
  customer_response_logged: { border: V2.errText, bg: V2.errSoft },
  resolution_email_sent:    { border: V2.okText, bg: V2.okSoft },
  lsm_return_update:        { border: V2.warnText, bg: V2.warnSoft },
  shopify_return_created:   { border: V2.infoText, bg: V2.infoSoft },
  shopify_return_closed:    { border: V2.okText, bg: V2.okSoft },
  shopify_return_deferred:  { border: V2.warnText, bg: V2.warnSoft },
  shopify_return_linked:    { border: V2.infoText, bg: V2.infoSoft },
  status_change:            { border: V2.ink45, bg: V2.hoverTint },
  note:                     { border: V2.warnText, bg: V2.warnSoft },
  file_added:               { border: V2.ink45, bg: V2.hoverTint },
  whatsapp_outbound_manual: { border: '#0d9488', bg: '#f0fdfa' },  // teal — distinct from vendor-response green; WCAG AA pass
  kanal_outbound_manual:    { border: V2.infoText, bg: V2.infoSoft },
  _inbound:                 { border: V2.agent, bg: V2.agentSoft },
  internal_to_vendor:       { border: V2.infoText, bg: V2.infoSoft },
  internal_to_customer:     { border: V2.infoText, bg: V2.infoSoft },
  internal_outbound:        { border: V2.ink70, bg: V2.hoverTint },
}

/** Types shown in the "Relevant" tab (default). Anything not listed only shows in "All". */
export const RELEVANT_COMM_TYPES = new Set([
  'email_sent',
  'vendor_response_logged',
  'customer_response_logged',
  'resolution_email_sent',
  'lsm_return_update',
  'shopify_return_created',
  'shopify_return_closed',
  'shopify_return_deferred',
  // 2026-07-29: a customer-initiated Shopify return that links to an EXISTING
  // case is not a staff echo — it is the customer act itself. Hiding it made
  // the webhook look broken (order 13406446485881 / RMA-2026-0497). Adding it
  // here also suppresses the duplicate v2 event-row via CaseTimeline's
  // dual-write dedup, so the curated comm row is the single Relevant surface.
  // Requires COMM_LABELS / COMM_ICON_MAP / COMM_ICON_COLORS entries above —
  // without them the row renders as lowercase English fallback text.
  'shopify_return_linked',
  'status_change',
  'note',
  // 2026-08-02 (board gap F): attachments land correctly in case.evidence,
  // but the file_added comm (evidence upload / inbox-attachment copy / legacy
  // migrate) was All-tab-only — the default view showed no trace that a file
  // ever arrived. (Until 2026-08-03 this predicate also fed the V2
  // Conversation tab; that tab now runs the STRICT filter — see
  // CONVERSATION_INTAKE_* below and `timeline/conversationView.js`.)
  'file_added',
  'whatsapp_outbound_manual',
  // S1-11b (Auflage A15a): derselbe Fall fuer die uebrigen Kanaele. Ohne den
  // Eintrag fiele die gemeldete Nachricht aus der Standardansicht heraus.
  'kanal_outbound_manual',
  'internal_to_vendor',
  'internal_to_customer',
  'internal_outbound',
])

/**
 * Event-kind entries that are explicitly relevant and must show in the Relevant tab.
 * Distinct from RELEVANT_COMM_TYPES because event rows have no comm counterpart —
 * they would be suppressed by the comm-dedup check if listed there.
 * Wiedervorlage lifecycle: all 5 snooze/reopen types are staff-relevant signals.
 * reopen_reverted + inbox_linked_silent: silent-link/reopen-undo lifecycle
 * (Task 8) — event-only rows (no comm counterpart), so they must be listed
 * here or the comm-dedup check below would suppress them from Relevant.
 */
export const RELEVANT_EVENT_TYPES = new Set([
  'snooze_set',
  'snooze_cancelled',
  'snooze_due',
  'snooze_broken_by_email',
  'case_reopened_by_email',
  'reopen_reverted',
  'inbox_linked_silent',
  // 2026-08-02 (board gap F): photo evidence arriving on an inbound customer
  // reply (record_event only, no comm counterpart). Listed explicitly instead
  // of relying on the CaseTimeline event-branch fall-through default, so a
  // future default-flip cannot silently hide file arrivals.
  'photo_attachment_received',
])

/**
 * Event-kind entries that are noise in the Relevant tab — All-only.
 * reactivation_suppressed: auto-reply suppression is an internal impl detail;
 * the absence of a case_reopened_by_email row already signals "suppressed".
 */
export const NOISE_EVENT_TYPES = new Set([
  'reactivation_suppressed',
])

// ── V2 Conversation tab — strict filter (Owner directive 2026-08-03) ─────────
//
// "Timeline-Iteration 2, PR 1": the Conversation tab carries ONLY
//   1. messages of every direction (the bubble classes — see
//      `timeline/conversationView.js` classifyConversationEntry),
//   2. hand-written notes (free-text `note` comms; machine-written notes stay
//      audit-only via the structural matchers in `timeline/toActivityEntry.js`),
//   3. attachment intakes — the two sets below,
//   4. manually added evidence ("wenn Evidenz manuell hinzugefügt wird, ist
//      das Timeline-relevant") — which needs no set of its own: the manual
//      evidence upload logs `_log_comm(case, "file_added", "Evidence
//      uploaded: …")` (backend/rma_routes.py, POST …/evidence), so class 4
//      IS a class-3 `file_added` comm. Verified 2026-08-03.
//   5. seit dem Owner-Entscheid vom 2026-09-21 die LSM-SYSTEMZEILE
//      (`lsm_return_update`) — sie hat KEINE Menge in dieser Datei, sondern
//      steht in `SYSTEM_LINE_COMM_TYPES` in `timeline/conversationView.js`,
//      weil sie kein Eingang ist, sondern eine eigene Klasse: zentriert, ohne
//      Absender, ohne `unverified`, mit gekürztem Betreff.
// Everything else (status_change, tracking_*, SLA, Shopify-return
// echoes, snooze lifecycle, …) lives ONLY in the Audit tab, which stays the
// complete unfiltered history.
//
// 🔴 Hier stand `lsm_*` in der Aufzählung des Audit-Rests, und das war seit
// dem 2026-09-21 falsch. Gefunden von der Regel-15-Wiedervorlage am
// 2026-09-22 als SECHSTE widersprechende Stelle, nachdem fünf andere schon
// korrigiert waren. Die Lehre steht dort: die Liste der Stellen nicht aus
// einem Bericht abschreiben, sondern über `grep -rln` die lebenden Flächen
// aufzählen und jede einzeln ansehen. The legacy (flag-OFF) Relevant tab keeps the
// RELEVANT_* predicate above unchanged.
//
// `photo_attachment_received` is event-only (record_event, no comm
// counterpart) and `file_added` is comm-only (no record_event writer), so
// including both cannot double-render one act.

/** Comm types that are attachment intakes — conversation-relevant interstitials. */
export const CONVERSATION_INTAKE_COMM_TYPES = new Set([
  'file_added',
])

/** Event types that are attachment intakes — conversation-relevant interstitials. */
export const CONVERSATION_INTAKE_EVENT_TYPES = new Set([
  'photo_attachment_received',
])
